import HotTable, {HotColumn, HotTableProps} from "@handsontable/react";
import {IconButton} from "@zendeskgarden/react-buttons";
import Handsontable from "handsontable";
import {CellSettings} from "handsontable/settings";
import React, {memo, ReactElement, useEffect, useMemo, useRef} from "react";
import {BiChevronDown, BiWrench} from "react-icons/bi";
import {toast} from "react-toastify";
import {GetAnalystRenovationsQuery, VersionType} from "../../../__generated__/generated_types";
import BarLoader from "../../../components/bar-loader/BarLoader";
import {LICENSES} from "../../../constants/Licenses";
import {Property} from "../../../contexts/properties/logic";
import {getMonths} from "../../../utils/months-helper";
import * as css from "./styles/reno-table.module.scss";
import {buildCellConfig, buildColHeaderHtml, buildRenoTableData, checkForValidationError, hideValuesInRow, RenovationTableRow, showValuesInRow, summarizePropertyColumns, summarizeUnitTypePackageCounts} from "./utils/reno-table-utils";


export interface AnalystRenovationsTableProps {
    property: Property;
    editAllowed: boolean;
    renovationData: UnitTypeWithRenovationPackages[];
    isLoading: boolean;
    onOpenDrawer: (unitTypeId: string, unitTypeName: string) => void;
    onChange: (changes: RenoTableOnChange[]) => void;
}

export interface UnitTypeWithRenovationPackages {
    id: string;
    name: string;
    unitTypeScheduleValues: {
        leaseExpirationCount: number;
        monthIndex: number;
        moveOutCount: number;
        moveOutRate: number;
        year: number;
        version: VersionType;
    }[];
    renovationPackages: GetAnalystRenovationsQuery["queryRenovationPackagesPaginated"]["items"];
}

export interface RenoTableOnChange {
    packageId: string;
    version: VersionType;
    monthIndex: number;
    value: number;
}

interface RenoTablePrevState {
    tableData: RenovationTableRow[];
    hiddenRows: number[];
    cellConfig: CellSettings[];
}

const undoRedoSkipSources = ["loadData", "updateData", "computedRowSummary", "computedColSummary", "ColumnSummary.reset"];
const userEntryInputSources = ["edit", "CopyPaste.paste", "UndoRedo.undo", "UndoRedo.redo", "Autofill.fill"];

export function AnalystRenovationsTable({property, editAllowed, renovationData, isLoading, onOpenDrawer, onChange}: AnalystRenovationsTableProps): ReactElement {
    const months = getMonths(property);
    const defaultPrevTableState: RenoTablePrevState = {tableData: [], hiddenRows: [], cellConfig: []};

    const hotRef = useRef<HotTable>(null);

    // these are used to maintain table state in between renders
    const expandedRowIds = useRef<Set<string>>(new Set());
    const unitTypeRowIndexes = useRef<Set<number>>(new Set());
    const propertySummaryContributionIndexes = useRef<Set<number>>(new Set());
    const prevDynamicData = useRef<RenoTablePrevState>({tableData: [], hiddenRows: [], cellConfig: []});

    const onChangeUpdateDebounce = useRef<ReturnType<typeof setTimeout>>();
    const onChangeUpdatesToMake = useRef<Map<string, RenoTableOnChange>>(new Map());

    useEffect(() => {
        expandedRowIds.current = new Set();
        unitTypeRowIndexes.current = new Set();
        propertySummaryContributionIndexes.current = new Set();
        prevDynamicData.current = defaultPrevTableState;
    }, [property]);

    // builds and maintains the data structure and config options to run the table
    const dynamicData = useMemo<{tableData: RenovationTableRow[], hiddenRows: number[], cellConfig: CellSettings[]}>(() => {
        if (!renovationData || renovationData.length == 0) {
            return {
                tableData: prevDynamicData.current.tableData,
                hiddenRows: prevDynamicData.current.hiddenRows,
                cellConfig: prevDynamicData.current.cellConfig,
            };
        }

        const builtRenoTableData = buildRenoTableData(renovationData, property, expandedRowIds.current);
        const tableData = builtRenoTableData.tableData;
        const hiddenRows = builtRenoTableData.hiddenRows;

        unitTypeRowIndexes.current = builtRenoTableData.unitTypeRowIndexes;
        propertySummaryContributionIndexes.current = builtRenoTableData.propertySummaryContributionIndexes;

        const cellConfig = buildCellConfig(tableData, expandedRowIds.current, property, editAllowed);

        // we were successful in getting new data and configs, so update our prev data ref
        prevDynamicData.current = {tableData, hiddenRows, cellConfig};

        return {tableData, hiddenRows, cellConfig};
    }, [renovationData]);

    // when the dynamicData is ready, we load the new data and settings into the hot instance, and then compute summary columns
    useEffect(() => {
        const hot = hotRef.current?.hotInstance;

        if (!hot || !dynamicData.tableData.length) {
            return;
        }

        hot.batch(() => {
            hot.suspendRender();

            hot.loadData(dynamicData.tableData);
            hot.updateSettings({cell: dynamicData.cellConfig, hiddenRows: {rows: dynamicData.hiddenRows}});

            hot.resumeRender();
        });

        const monthColumns = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25]);
        const updatesToMake = [
            ...summarizeUnitTypePackageCounts(unitTypeRowIndexes.current, monthColumns, hot),
            ...summarizePropertyColumns(propertySummaryContributionIndexes.current, monthColumns, hot),
        ];
        hot.setDataAtCell(updatesToMake, "computedColSummary");

        hot.render();
    }, [renovationData]);

    function toggleHiddenRows(rowData: RenovationTableRow) {
        const hot = hotRef.current?.hotInstance;
        const hiddenRowsPlugin = hot?.getPlugin("hiddenRows");

        if (!hot || !hiddenRowsPlugin || !rowData.childRowIndexes) {
            return false;
        }

        const currentlyHiddenRows = hiddenRowsPlugin.getHiddenRows();
        const hiddenChildRowIndex = rowData?.childRowIndexes?.[0] ?? undefined;

        if (hiddenChildRowIndex != undefined && currentlyHiddenRows.includes(hiddenChildRowIndex)) {
            if (rowData.rowId) {
                expandedRowIds.current.add(rowData.rowId);
            }
            hiddenRowsPlugin.showRows(rowData.childRowIndexes);
            hideValuesInRow(rowData.rowIndex, hot);
        } else {
            if (rowData.rowId) {
                expandedRowIds.current.delete(rowData.rowId);
            }
            hiddenRowsPlugin.hideRows(rowData.childRowIndexes);
            showValuesInRow(rowData.rowIndex, hot);
        }

        // keeps the hidden row state in sync if api refetch triggers data update
        prevDynamicData.current.hiddenRows = hiddenRowsPlugin.getHiddenRows();

        hot.render();
    }

    function LabelColumnRenderer(props: Handsontable.ColumnSettings): ReactElement {
        const hot = hotRef.current?.hotInstance;

        if (!hot) {
            return <></>;
        }

        const rowData = hot ? hot.getSourceDataAtRow(props["row"]) as RenovationTableRow | undefined : undefined;
        let isExpanded, showExpandCollapse;

        if (rowData && rowData.hasPackages) {
            const hiddenRowsPlugin = hot?.getPlugin("hiddenRows");
            isExpanded = hiddenRowsPlugin ? !hiddenRowsPlugin.isHidden(props["row"] + 1) : false;
            showExpandCollapse = rowData.hasPackages;
        }

        const td = props["TD"];

        if (td && rowData) {
            td.className = css.rowHeader;

            if (["renoPackage", "totalRenovations", "totalExpiringMoveOuts"].includes(rowData.rowType)) {
                td.className = td.className + ` ${css.textAlignRight}`;
            }

            if (["totalRenovations", "totalExpiringMoveOuts"].includes(rowData.rowType)) {
                td.className = td.className + ` ${css.greyCell}`;
            }

            if (rowData.rowType == "totalRenovations") {
                td.className = td.className + ` ${css.thickBorderTop}`;
            }

            if (props["row"] == 0) {
                td.className = td.className + ` ${css.thickBorderBottom}`;
            }
        }

        return (
            <React.Fragment>
                {rowData && rowData.rowType == "unitType" && editAllowed &&
                    <IconButton
                        className={css.rowActionButton}
                        size="small"
                        isBasic
                        onClick={() => {
                            if (rowData.rowId) {
                                onOpenDrawer(rowData.rowId, rowData.label);
                            }
                        }}
                        aria-label={`configure renovation packages for unit type ${rowData.label}`}
                    >
                        <BiWrench />
                    </IconButton>
                }
                {props["value"]}
                <IconButton
                    className={`${css.rowActionButton} ${css.expandCollapse} ${!showExpandCollapse && css.isHidden}`}
                    isRotated={isExpanded}
                    size="small"
                    onClick={() => {
                        if (rowData && rowData.hasPackages) {
                            toggleHiddenRows(rowData);
                        }
                    }}
                    aria-label={`${isExpanded ? "hide" : "show"} renovation packages for unit type ${rowData ? rowData.label : ""}`}
                >
                    <BiChevronDown />
                </IconButton>
            </React.Fragment>
        );
    }

    function TotalsColumnRenderer(props: Handsontable.ColumnSettings): ReactElement {
        const hot = hotRef.current?.hotInstance;

        if (!hot) {
            return <></>;
        }

        const versionType = props["versionType"];
        const rowData = hot ? hot.getSourceDataAtRow(props["row"]) as RenovationTableRow | undefined : undefined;
        const rowValues = hot.getDataAtRow(props["row"]);

        let total: string | number = 0;
        if (rowValues.length > 0 && rowData && rowData.rowType) {
            if (versionType == VersionType.Reforecast) {
                total = rowValues.slice(1, 13).sum();
            } else {
                total = rowValues.slice(14, 26).sum();
            }

            if (rowData.rowType == "totalExpiringMoveOuts") {
                total = total.toFixed(1);
            }
        }

        const td = props["TD"];

        if (td) {
            td.className = css.greyCell;

            if (props["row"] == 0) {
                td.className = td.className + ` ${css.thickBorderBottom}`;
            }
        }

        return (
            <React.Fragment>
                {total}
            </React.Fragment>
        );
    }

    function handleOnChange(changes: RenoTableOnChange[]) {
        if (!onChange) {
            return;
        }

        if (onChangeUpdateDebounce.current) {
            clearTimeout(onChangeUpdateDebounce.current);
        }

        for (const change of changes) {
            const {packageId, version, monthIndex} = change;
            onChangeUpdatesToMake.current.set(`${packageId}_${version}_${monthIndex}`, change);
        }

        onChangeUpdateDebounce.current = setTimeout(() => {
            if (onChangeUpdatesToMake.current.size == 0) {
                return;
            }

            onChange(Array.from(onChangeUpdatesToMake.current.values()));
            onChangeUpdatesToMake.current.clear();
        }, 500);
    }

    const hotTableSettings: HotTableProps = {
        width: "100%",
        height: "auto",
        stretchH: "all",
        columnHeaderHeight: 70,
        rowHeights: 48,
        manualColumnResize: false,
        autoColumnSize: false,
        licenseKey: LICENSES.HandsOnTable,
        readOnly: true,
        fixedColumnsLeft: 1,
        fixedRowsTop: 1,
        renderAllRows: true,
        // there is a bug with disableVisualSelection and hiddenRows
        // this is to keep the label column from being selected
        afterSelection(this: Handsontable, _row, col) {
            if (col == 0) {
                this.deselectCell();
            }
        },
        beforeUndoStackChange: (_doneActions, _source) => {
            const source = _source as string;
            if (undoRedoSkipSources.includes(source)) {
                return false;
            }
        },
        beforeChange(changes) {
            const changesToValidate = [];

            for (let i = 0; i < changes.length; i++) {
                const change = changes[i];

                if (!change) {
                    continue;
                }

                // change[3] represents the new value waiting to be entered
                // if a user presses the delete key on a cell, it will register the change as an empty string
                // so, we set it to null to make it a valid api value
                if (change[3] == "") {
                    change[3] = null;
                }

                changesToValidate.push(change[3]);
            }

            const errorMessage = checkForValidationError(changesToValidate);

            // if the data does not pass validate, abort changes and display reasons why
            if (errorMessage) {
                toast.error(errorMessage);
                return false;
            }
        },
        afterChange(this: Handsontable, changes, _source) {
            const source = _source as string;

            if (changes && userEntryInputSources.includes(source)) {
                const changedData: RenoTableOnChange[] = [];
                const colsImpacted = new Set<number>();
                const parentRowsImpacted = new Set<number>();
                const updatesToMake: [number, number, (number | null)][] = [];

                for (const [row, col, oldVal, newVal] of changes) {
                    if (row == undefined || col == undefined || newVal === undefined || oldVal === newVal) {
                        continue;
                    }

                    let colIndex = col;
                    let version, monthIndex;

                    if (typeof colIndex === "string") {
                        [version, monthIndex] = colIndex.split(".");
                        colIndex = this.propToCol(colIndex);
                    }

                    const physicalRow = this.toPhysicalRow(row);
                    const physicalColumn = this.toPhysicalColumn(colIndex);
                    const rowData: RenovationTableRow | undefined = this.getSourceDataAtRow(physicalRow) as RenovationTableRow | undefined;

                    if (rowData && rowData.rowType == "renoPackage") {
                        const {parentRowIndex, rowId} = rowData;

                        if (rowId && version && monthIndex != null) {
                            changedData.push({
                                packageId: rowId,
                                version: version.includes("rfcst") ? VersionType.Reforecast : VersionType.Budget,
                                monthIndex: +monthIndex,
                                value: newVal,
                            });
                        }

                        if (parentRowIndex) {
                            colsImpacted.add(physicalColumn);
                            parentRowsImpacted.add(parentRowIndex);
                        }
                    }
                }

                updatesToMake.push(...summarizeUnitTypePackageCounts(parentRowsImpacted, colsImpacted, this));
                updatesToMake.push(...summarizePropertyColumns(propertySummaryContributionIndexes.current, colsImpacted, this));

                handleOnChange(changedData);
                this.setDataAtCell(updatesToMake, "computedColSummary");
            }
        },
    };

    return (
        <div className={css.renoTableWrapper}>
            <div className={css.renoTable}>
                <BarLoader loading={isLoading} zIndex={9} />
                <HotTable
                    ref={hotRef}
                    {...hotTableSettings}
                >
                    <HotColumn data="label" width={300} disableVisualSelection={true}>
                        <LabelColumnRenderer hot-renderer />
                    </HotColumn>

                    {months.map((month, index) =>
                        <HotColumn
                            key={`rfcstValues.${index}`}
                            data={`rfcstValues.${index}`}
                            type="numeric"
                            width={80}
                            title={buildColHeaderHtml(month, index + 1, property)}
                        />
                    )}

                    <HotColumn title={buildColHeaderHtml("Total", 13, property)} width={80}>
                        <TotalsColumnRenderer versionType={VersionType.Reforecast} hot-renderer />
                    </HotColumn>

                    {months.map((month, index) =>
                        <HotColumn
                            key={`bdgtValues.${index}`}
                            data={`bdgtValues.${index}`}
                            type="numeric"
                            width={80}
                            title={buildColHeaderHtml(month, index + 14, property)}
                        />
                    )}

                    <HotColumn title={buildColHeaderHtml("Total", 26, property)} type="numeric" numericFormat={{pattern: "0.0"}} width={80}>
                        <TotalsColumnRenderer versionType={VersionType.Budget} hot-renderer />
                    </HotColumn>
                </HotTable>
            </div>
        </div>
    );
}

export const AnalystRenovationsTableMemo = memo(
    AnalystRenovationsTable,
    (prev, next) => {
        return (
            prev.property === next.property &&
            prev.renovationData === next.renovationData
        );
    }
);
